import { useState, useRef, useCallback } from 'react';
import { WHATSAPP_NUMBER, WHATSAPP_MESSAGE, GOOGLE_MAPS_URL } from '@/lib/index';
import { MenuModal } from '@/components/MenuModal';
import { IMAGES } from '@/assets/images';
import { motion } from 'framer-motion';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';
import { Phone, Mic, Gift, MapPin, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { SiWhatsapp } from 'react-icons/si';

// ── Full-screen horizontal swipe slider ──────────────────────────────────────
interface SwipeSliderProps {
  slides: { src: string; alt: string; caption?: string }[];
  height?: string;
}

function SwipeSlider({ slides, height = 'h-screen' }: SwipeSliderProps) {
  const [current, setCurrent] = useState(0);
  const startX = useRef<number | null>(null);
  const isDragging = useRef(false);

  const prev = useCallback(() => setCurrent((c) => (c - 1 + slides.length) % slides.length), [slides.length]);
  const next = useCallback(() => setCurrent((c) => (c + 1) % slides.length), [slides.length]);

  const onPointerDown = (e: React.PointerEvent) => {
    startX.current = e.clientX;
    isDragging.current = false;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (startX.current !== null && Math.abs(e.clientX - startX.current) > 5) {
      isDragging.current = true;
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (startX.current === null) return;
    const dx = e.clientX - startX.current;
    if (isDragging.current && Math.abs(dx) > 50) {
      if (dx < 0) next(); else prev();
    }
    startX.current = null;
    isDragging.current = false;
  };

  return (
    <div
      className={`relative w-full ${height} overflow-hidden select-none touch-pan-y cursor-grab active:cursor-grabbing`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* Slides — each slide is exactly 100vw wide, track shifts by current*100vw */}
      <div
        className="flex h-full transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(calc(-${current} * 100vw))` }}
      >
        {slides.map((slide, i) => (
          <div key={i} className="relative flex-shrink-0" style={{ width: '100vw' }}>
            <img
              src={slide.src}
              alt={slide.alt}
              className="w-full h-full object-cover pointer-events-none"
              draggable={false}
            />
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10" />
            {/* Caption */}
            {slide.caption && (
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <span className="inline-block bg-black/60 text-white text-sm md:text-base font-semibold px-5 py-2 rounded-full border border-white/30 backdrop-blur-sm">
                  {slide.caption}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/70 transition-colors border border-white/20"
        aria-label="Previous"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/70 transition-colors border border-white/20"
        aria-label="Next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === current ? 'bg-white scale-125' : 'bg-white/50'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Slide counter */}
      <div className="absolute top-4 right-4 z-10 bg-black/40 text-white text-xs px-3 py-1 rounded-full border border-white/20 backdrop-blur-sm">
        {current + 1} / {slides.length}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
const KTV_CAPTION = '🎤 Free KTV · No Minimum Spend';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  // Gallery slides — room/KTV images get a special caption
  const gallerySlides = [
    { src: IMAGES.IMG_6007_35, alt: 'Restaurant interior 1' },
    { src: IMAGES.IMG_6012_36, alt: 'Restaurant interior 2' },
    { src: IMAGES.IMG_6013_37, alt: 'Restaurant interior 3' },
    { src: IMAGES.IMG_6011_38, alt: 'Restaurant interior 4' },
    { src: IMAGES.IMG_6009_39, alt: 'Restaurant interior 5' },
    { src: IMAGES.IMG_6006_40, alt: 'Restaurant interior 6' },
    { src: IMAGES.IMG_6010_41, alt: 'Restaurant interior 7' },
    { src: IMAGES.IMG_6458_42, alt: 'Restaurant interior 8' },
    { src: IMAGES.A62CA056B206A947B3E492E49286DDFA_48, alt: 'Private room', caption: KTV_CAPTION },
    { src: IMAGES.F7AD4DC88EAE2DB5C7EEAFE47DA1ECFA_43, alt: 'Private room with KTV', caption: KTV_CAPTION },
    { src: IMAGES.IMG_3591_44, alt: 'Restaurant interior 11' },
    { src: IMAGES.IMG_1EC8C404_8166_4A3C_9138_BD7CA5349D86_46, alt: 'Restaurant interior 12' },
  ];

  // Food showcase slides — the 5 user-uploaded food photos
  const foodSlides = [
    { src: IMAGES.FOOD_PHOTO_1, alt: 'Authentic Chongqing Hot Pot' },
    { src: IMAGES.FOOD_PHOTO_2, alt: 'Signature dishes' },
    { src: IMAGES.FOOD_PHOTO_3, alt: 'Chongqing flavors' },
    { src: IMAGES.FOOD_PHOTO_4, alt: 'Fresh ingredients' },
    { src: IMAGES.FOOD_PHOTO_5, alt: 'Hot pot spread' },
  ];

  const globalCities = [
    { image: IMAGES.GLOBAL_CITY_9,  label: 'Paris, France' },
    { image: IMAGES.GLOBAL_CITY_5,  label: 'Rome, Italy' },
    { image: IMAGES.GLOBAL_CITY_6,  label: 'Singapore' },
    { image: IMAGES.GLOBAL_CITY_10, label: 'London, UK' },
    { image: IMAGES.GLOBAL_CITY_1,  label: 'Europe' },
    { image: IMAGES.GLOBAL_CITY_4,  label: 'Asia Pacific' },
  ];

  return (
    <div className="min-h-screen bg-primary">

      {/* ── HERO: full cover image, no text overlay ── */}
      <section id="hero" className="relative min-h-screen flex flex-col items-center justify-end overflow-hidden">
        <img
          src={IMAGES.IMAGE_32}
          alt="Liuyishou Chongqing Hot Pot"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

        <motion.div
          className="relative z-10 flex flex-col sm:flex-row gap-5 justify-center items-center pb-16 px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.4 }}
        >
          <motion.a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-10 py-5 bg-[#25D366] text-white text-lg font-semibold rounded-full hover:bg-[#20BA5A] transition-all duration-200 shadow-xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={springPresets.snappy}
          >
            <SiWhatsapp className="w-6 h-6" />
            BOOK A TABLE
          </motion.a>

          <motion.button
            onClick={() => setIsMenuOpen(true)}
            className="inline-flex items-center gap-3 px-10 py-5 bg-white/10 text-white text-lg font-semibold rounded-full border-2 border-white hover:bg-white hover:text-primary transition-all duration-200 shadow-xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={springPresets.snappy}
          >
            VIEW MENU
          </motion.button>
        </motion.div>
      </section>

      {/* ── FOOD SHOWCASE: full-screen swipeable ── */}
      <section id="food" className="relative">
        {/* Section label overlay */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 text-center pointer-events-none">
          <div className="bg-black/50 backdrop-blur-sm px-8 py-3 rounded-full border border-white/30">
            <p className="text-accent font-bold text-base tracking-widest uppercase">Authentic Chongqing Hot Pot</p>
            <p className="text-white text-sm tracking-wider">Chinese Cuisine · 正宗重庆火锅</p>
          </div>
        </div>
        <SwipeSlider slides={foodSlides} height="h-screen" />
      </section>

      {/* ── GLOBAL PRESENCE ── */}
      <section id="global" className="py-24 px-4 bg-primary">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={springPresets.gentle}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Globe className="w-10 h-10 text-accent" />
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              A Global Taste of Chongqing
            </h2>
            <p className="text-white/80 text-xl max-w-2xl mx-auto leading-relaxed">
              From Chongqing to the world — Liuyishou has grown into a global hot pot empire
              with over <span className="text-accent font-bold text-2xl">1,500 locations</span> across
              Asia, Europe, North America, the Middle East and beyond.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="grid grid-cols-3 gap-6 mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
          >
            {[
              { number: '1,500+', label: 'Locations Worldwide' },
              { number: '20+', label: 'Countries & Regions' },
              { number: '24', label: 'Years of Excellence' },
            ].map((stat) => (
              <div key={stat.label} className="text-center bg-white/10 rounded-2xl py-8 px-4 border border-white/20">
                <p className="text-4xl md:text-5xl font-bold text-accent mb-2">{stat.number}</p>
                <p className="text-white/80 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </motion.div>

          {/* City photo grid */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 gap-5"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {globalCities.map((city, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3]"
                whileHover={{ scale: 1.02 }}
                transition={springPresets.snappy}
              >
                <img
                  src={city.image}
                  alt={city.label}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-4 text-white font-semibold text-lg drop-shadow-lg">
                  {city.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section id="about" className="py-24 px-4 bg-[#AA0000]">
        <div className="container mx-auto max-w-6xl">
          <motion.h2
            className="text-5xl md:text-6xl font-bold mb-16 text-center text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={springPresets.gentle}
          >
            Why Choose Us
          </motion.h2>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
          >
            <motion.div
              variants={staggerItem}
              className="bg-white/10 rounded-3xl p-10 hover:bg-white/15 transition-all duration-300 border border-white/20"
              whileHover={{ scale: 1.02, y: -5 }}
              transition={springPresets.snappy}
            >
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-6">
                <Mic className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Private KTV Rooms</h3>
              <p className="text-white/80 text-lg leading-relaxed">
                Enjoy your meal in one of our exclusive private dining rooms equipped with karaoke — perfect for parties, gatherings and celebrations. <span className="text-accent font-semibold">Free KTV, no minimum spend.</span>
              </p>
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="bg-white/10 rounded-3xl p-10 hover:bg-white/15 transition-all duration-300 border border-white/20"
              whileHover={{ scale: 1.02, y: -5 }}
              transition={springPresets.snappy}
            >
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-6">
                <Gift className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Free Celebration Decoration</h3>
              <p className="text-white/80 text-lg leading-relaxed">
                Celebrating a birthday or special occasion? We will decorate your table for free to make your moment truly unforgettable.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── RESTAURANT GALLERY: full-screen swipeable ── */}
      <section id="gallery" className="relative">
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 text-center pointer-events-none">
          <div className="bg-black/50 backdrop-blur-sm px-8 py-3 rounded-full border border-white/30">
            <p className="text-accent font-bold text-base tracking-widest uppercase">Inside Our Restaurant</p>
            <p className="text-white text-sm tracking-wider">Swipe to explore our dining rooms</p>
          </div>
        </div>
        <SwipeSlider slides={gallerySlides} height="h-screen" />
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="py-24 px-4 bg-[#AA0000]">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h2
            className="text-5xl md:text-6xl font-bold mb-12 text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={springPresets.gentle}
          >
            Find Us
          </motion.h2>

          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
          >
            <div className="flex items-center justify-center gap-3 text-white text-2xl">
              <Phone className="w-7 h-7 text-accent" />
              <a href={`tel:+${WHATSAPP_NUMBER}`} className="hover:text-accent transition-colors">
                +{WHATSAPP_NUMBER}
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <motion.a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-primary text-lg font-semibold rounded-full hover:bg-accent transition-all duration-200 shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={springPresets.snappy}
              >
                <MapPin className="w-5 h-5" />
                View on Google Maps
              </motion.a>

              <motion.a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white text-lg font-semibold rounded-full hover:bg-[#20BA5A] transition-all duration-200 shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={springPresets.snappy}
              >
                <SiWhatsapp className="w-5 h-5" />
                Reserve via WhatsApp
              </motion.a>
            </div>
          </motion.div>

          <motion.footer
            className="mt-20 pt-8 border-t border-white/20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, delay: 0.4 }}
          >
            <p className="text-white/60 text-sm">
              © 2026 Liuyishou Chongqing Hot Pot. All rights reserved.
            </p>
          </motion.footer>
        </div>
      </section>

      <MenuModal isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </div>
  );
}
